/**
 * Headless Shopify analytics for the reframeyouth.com storefront.
 *
 * Shopify no longer serves these pages, so we report page views and product
 * views ourselves via @shopify/hydrogen-react's supported analytics API.
 * Combined with the `_shopify_y` / `_shopify_s` cookies (set by
 * `useShopifyCookies` in App.tsx), this lets Shopify admin analytics show
 * sessions, page views, product views, and conversion attribution again.
 */
import type { ShopifyPageViewPayload, ShopifyAnalyticsProduct } from "@shopify/hydrogen-react";
import { afterFirstInteraction } from "./interaction";
let sdkPromise: Promise<typeof import("./shopifyAnalyticsSdk")> | undefined;
const sdk = () => sdkPromise ??= import("./shopifyAnalyticsSdk");
let idleReady: Promise<void> | undefined;
const idle = () => idleReady ??= new Promise<void>((resolve) => afterFirstInteraction(resolve));
type AnalyticsLocation = { url: string; path: string; referrer: string; title: string };
function currentLocation(): AnalyticsLocation {
  return { url: window.location.href, path: window.location.pathname, referrer: document.referrer, title: document.title };
}

/** gid + currency for reframeyouth.myshopify.com (looked up via Admin API). */
export const SHOP_ID = "gid://shopify/Shop/69300289820";
export const SHOP_CURRENCY = "USD";

type ProductIdEntry = {
  productGid: string;
  variants: { gid: string; title: string; price: string }[];
};

/**
 * Send monorail events through our own domain (same-origin relay in
 * api-server) so Shopify's HttpOnly _shopify_essential/_shopify_analytics
 * cookies ride along. Since the 2026-04 cookie deprecation, events sent
 * directly to monorail-edge.shopifysvc.com are accepted (HTTP 200) but no
 * longer attributed to sessions in Shopify Analytics.
 * hydrogen-react builds the URL as `https://${shopDomain}/.well-known/...`,
 * so include the /api base path in the "domain".
 */
function monorailRelayDomain(): string | undefined {
  if (window.location.protocol !== "https:") return undefined;
  const base = import.meta.env.BASE_URL; // "/" for this root artifact
  return `${window.location.host}${base}api`;
}

function basePayload(lib: Awaited<ReturnType<typeof sdk>>, location: AnalyticsLocation): ShopifyPageViewPayload {
  return {
    ...lib.getClientBrowserParameters(),
    url: location.url,
    path: location.path,
    search: new URL(location.url).search,
    referrer: location.referrer,
    title: location.title,
    shopId: SHOP_ID,
    currency: SHOP_CURRENCY,
    acceptedLanguage: "EN",
    hasUserConsent: true,
  };
}

function hasShopifyVisitorCookie(): boolean {
  return /(?:^|;\s*)_shopify_y=[^;]+/.test(document.cookie);
}

/**
 * A single shared promise that resolves once the official `_shopify_y`
 * visitor cookie exists (or after an 8s cap, so events degrade gracefully
 * rather than pile up forever). One poller total, regardless of how many
 * events are waiting.
 */
let trackingReady: Promise<void> | null = null;

function getTrackingReady(): Promise<void> {
  if (!trackingReady) {
    trackingReady = new Promise<void>((resolve) => {
      if (hasShopifyVisitorCookie()) {
        resolve();
        return;
      }
      const startedAt = Date.now();
      const timer = window.setInterval(() => {
        if (hasShopifyVisitorCookie() || Date.now() - startedAt > 8000) {
          window.clearInterval(timer);
          resolve();
        }
      }, 200);
    });
  }
  return trackingReady;
}

/**
 * Serial send queue: events wait for the visitor cookie, then flush in the
 * exact order they were requested, so Shopify never records a later route
 * before an earlier one.
 *
 * Why wait at all: `useShopifyCookies({ fetchTrackingValues: true })` obtains
 * `_shopify_y` asynchronously via the Storefront API proxy. Events sent
 * before it lands carry no visitor token and Shopify Analytics silently
 * drops them — which loses the initial page view of nearly every visit.
 */
let sendChain: Promise<void> = Promise.resolve();

function cookieValue(name: string): string | null {
  const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
  return m ? decodeURIComponent(m[1]) : null;
}

/**
 * Also log the page view to our own database (first-party analytics), using
 * the same visitor/session tokens Shopify uses so the admin Traffic tab can
 * be compared 1:1 against the Shopify dashboard.
 */
function recordFirstPartyPageView(location: AnalyticsLocation): void {
  try {
    fetch(`${import.meta.env.BASE_URL}api/page-views`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        path: location.path,
        visitorToken: cookieValue("_shopify_y"),
        sessionToken: cookieValue("_shopify_s"),
        referrer: location.referrer || null,
      }),
    }).catch(() => {});
  } catch {
    /* best-effort */
  }
}

function whenTrackingReady(send: (lib: Awaited<ReturnType<typeof sdk>>, location: AnalyticsLocation) => void | Promise<void>): void {
  const location = currentLocation();
  sendChain = sendChain
    .then(idle)
    .then(getTrackingReady)
    .then(async () => {
      recordFirstPartyPageView(location);
      await send(await sdk(), location);
    })
    .catch(() => {
      /* analytics is best-effort; never break the chain */
    });
}

/** Fire a Shopify page-view event (generic, non-product pages). */
export function trackShopifyPageView(): void {
  whenTrackingReady((lib, location) => {
    return lib.sendShopifyAnalytics(
      {
        eventName: lib.AnalyticsEventName.PAGE_VIEW,
        payload: basePayload(lib, location),
      },
      monorailRelayDomain(),
    ).catch(() => {
      /* analytics is best-effort; never break the page */
    });
  });
}

export type ProductViewInput = {
  handle: string;
  title: string;
  /** 0-based index of the currently selected variant, if any. */
  variantIndex?: number;
};

/**
 * Fire a Shopify product page-view event with real product/variant GIDs.
 * Silently degrades to a plain page view if the handle is unknown.
 */
export function trackShopifyProductView(input: ProductViewInput): void {
  whenTrackingReady((lib, location) => {
  const entry = (lib.SHOPIFY_PRODUCT_IDS as Record<string, ProductIdEntry>)[input.handle];
  if (!entry) return lib.sendShopifyAnalytics({
    eventName: lib.AnalyticsEventName.PAGE_VIEW, payload: basePayload(lib, location),
  }, monorailRelayDomain());
  const variant =
    entry.variants[input.variantIndex ?? 0] ?? entry.variants[0];
  const product: ShopifyAnalyticsProduct = {
    productGid: entry.productGid,
    name: input.title,
    brand: "Reframe Youth",
    price: variant?.price ?? "0.00",
    ...(variant
      ? { variantGid: variant.gid, variantName: variant.title }
      : {}),
  };
    return lib.sendShopifyAnalytics(
      {
        eventName: lib.AnalyticsEventName.PAGE_VIEW,
        payload: {
          ...basePayload(lib, location),
          pageType: lib.AnalyticsPageType.product,
          resourceId: entry.productGid,
          products: [product],
          totalValue: variant ? parseFloat(variant.price) : undefined,
        },
      },
      monorailRelayDomain(),
    ).catch(() => {
      /* best-effort */
    });
  });
}
